/* global __WEB__ */

import map from 'lodash/map';
import throttler from './throttler';
import { buildRestUrl, buildSocketUrl } from './oms';
import {
    socketConnect,
    socketSubscribe,
    socketUnsubscribe,
} from '../actions/socket';

/**
 * This is a subscriber class that connects to a websocket to
 * deliver real time table data from an API endpoint.
 *
 * If websockets aren't supported by the client browser, the
 * class will fallback to using regular REST calls to fetch the data.
 *
 * @TODO currently the class needs a fallback action to enable fallback
 * to REST. This should be optimized in such a way that it won't be
 * necessary to pass a fallback action, but rather doing the REST
 * call directly in the class itself.
 *
 * @TODO implement itemSector, and make sources optional.
 */
class TableSubscriber {
    constructor() {
        // Keep a reference to params we subscribe to by channel-id
        // [channel-id]: [params]
        this.channels = {};

        this.data = [];

        // Since there's potentially a lot of rows in the recieved tables,
        // we have to add them to an array to be dispatched asyncronously.
        this.initialDataSent = false;
        this.hasSent = false;
    }

    start(create, update, fetchFallback = null, dispatch) {
        this.dispatch = dispatch;
        if (create && typeof create !== 'undefined') {
            this.createDispatch = (...args) => create(...args);
        }

        if (update && typeof update !== 'undefined') {
            // Create dispatch function which is throttled,
            // to be called maximun once per 750 ms per ticker.
            this.updateDispatch = throttler((...args) => {
                update(...args);
            }, 750);
        }

        if (fetchFallback && typeof fetchFallback !== 'undefined') {
            this.fetchFallback = (...args) => fetchFallback(...args);
        }

        this.connect();
    }

    connect() {
        if (window.WebSocket) {
            this.url = buildSocketUrl();
            this.webSockets = true;
            this.dispatch(socketConnect({ url: this.url }));
        } else {
            // Set the REST url instead
            this.url = buildRestUrl();
            this.webSockets = false;
        }
    }

    buildQuery(params, channel = null) {
        /* eslint-disable */
        const {
            columns,
            convert,
            leftJoins,
            sources,
            view,
            limit,
            offset,
            filter,
            start,
            itemSector,
        } = params;
        /* eslint-enable */

        const queryOptions = { columns: columns.join(',') };

        if (channel) {
            queryOptions.channel = channel;
        }

        if (
            (!sources || typeof sources === 'undefined')
            && (!itemSector || typeof itemSector === 'undefined')
        ) {
            // Checks if both itemSector and sources is empty
            // and returns false (resulting in a break)
            return false;
        } else if (!sources || typeof sources === 'undefined') {
            // if sources isn't set, we know that itemSector is set,
            // and use that.
            queryOptions.itemSector = itemSector;
        } else {
            // In the last case, we know that sources is set.
            // If the sources param is an array we must use generators
            // to pass the source for the API, orherwise we simply use
            // source to pass it.
            if (Array.isArray(sources) && sources.length > 1) {
                sources.forEach((source, i) => {
                    const key = `generators[${i}][source]`;

                    queryOptions[key] = `${source} as source`;
                });
            } else if (Array.isArray(sources)) {
                queryOptions.source = sources[0];
            } else {
                queryOptions.source = sources;
            }
        }

        // leftJoins is an optional parameter, and must be checked
        if (leftJoins && typeof leftJoins !== 'undefined') {
            queryOptions.leftJoins = leftJoins;
        }

        if (convert && typeof convert !== 'undefined') {
            queryOptions.convert = convert;
        }

        if (view && typeof view !== 'undefined') {
            queryOptions.view = view;
        }

        if (limit && typeof limit !== 'undefined') {
            queryOptions.limit = limit;
        }

        if (offset && typeof offset !== 'undefined') {
            queryOptions.offset = offset;
        }

        if (filter && typeof filter !== 'undefined') {
            queryOptions.filter = filter;
        }

        if (start && typeof start !== 'undefined') {
            queryOptions.start = start;
        }

        if (itemSector && typeof itemSector !== 'undefined') {
            queryOptions.itemSector = itemSector;
        }

        // Return the query with a URL format that can be passed to the message
        return map(queryOptions, (value, key) => `${key}=${value}`).join('&');
    }

    unsubscribe(channel) {
        if (!channel) {
            return;
        }

        // Clean up, so we can subscribe to the symbol again
        if (this.channels.hasOwnProperty(channel)) {
            delete this.channels[channel];
        }

        this.dispatch(socketUnsubscribe({ channel }));
    }

    subscribe(channel, params) {
        // @TODO
        // May have to implement params signature check
        // to see if we're already subscribing to it

        if (this.webSockets) {
            const query = this.buildQuery(params, channel);

            if (!query) {
                return false;
            }

            this.dispatch(socketSubscribe({
                channel,
                query,
                onMessage: this.onMessageHandler.bind(this),
            }));

            return channel;
        }

        if (this.fetchFallback) {
            const query = this.buildQuery(params);

            this.fetchFallback(`${this.url}?${query}`, params);
        }

        return false;
    }

    onMessageHandler(data) {
        try {
            if (typeof data.type === 'undefined') {
                return;
            }

            if (data.type === 'meta' && data.values) {
                this.initialDataSent = data.values.initial_data_sent || false;

                // If we get the message that all data is successfully sent,
                // we dispatch the create action with the array of ticker objects
                if (
                    this.initialDataSent
                    && typeof this.initialDataSent !== 'undefined'
                    && this.data.length > 0
                ) {
                    this.createDispatch(this.data);
                    this.initialDataSent = false;
                    this.data = [];
                }
            }

            // Append a new ticker object to the data list
            if (data.type === 'new' && this.createDispatch) {
                if (this.initialDataSent && this.data.length === 0) {
                    this.createDispatch([{ key: data.key, values: data.values }]);
                } else {
                    this.data.push({ key: data.key, values: data.values });
                }
            }

            // Dispatch an update action with the new ticker values
            if (data.type === 'update' && this.updateDispatch) {
                this.updateDispatch(data.key, data.values);
            }
        } catch (e) {
            // ignore error
        }
    }
}

export default TableSubscriber;



/** WEBPACK FOOTER **
 ** /src/shared/service/TableSubscriber.js
 **/